import { getWindow } from "./window";

export interface StorageHandler {
  isKeyExists(key: string): boolean;
  get(key: string, defaultValue: any): any;
  remove(key: string): void;
  set(key: string, value: any): void;
}

export const createLocalStorageHandler = (): StorageHandler => {
  return {
    isKeyExists: (key: string) => {
      return isKeyInLocalStorage(key);
    },
    get: (key: string, defaultValue: any) => {
      return getFromLocalStorage(key, defaultValue);
    },
    remove: (key: string) => {
      removeFromLocalStorage(key);
    },
    set: (key: string, value: any) => {
      setToLocalStorage(key, value);
    },
  };
};

export const createSessionStorageHandler = (): StorageHandler => {
  return {
    isKeyExists: (key: string) => {
      return isKeyInSessionStorage(key);
    },
    get: (key: string, defaultValue: any) => {
      return getFromSessionStorage(key, defaultValue);
    },
    remove: (key: string) => {
      removeFromSessionStorage(key);
    },
    set: (key: string, value: any) => {
      setToSessionStorage(key, value);
    },
  };
};

export const createInMemoryStorageHandler = (): StorageHandler => {
  const storage: { [key: string]: any } = {};

  return {
    isKeyExists: (key: string) => key in storage,
    get: (key: string, defaultValue: any) => (key in storage ? storage[key] : defaultValue),
    remove: (key: string) => {
      delete storage[key];
    },
    set: (key: string, value: any) => {
      storage[key] = value;
    },
  };
};

export const getFromLocalStorage = (key: string, defaultValue: any) => {
  if (typeof window === "undefined") return null;
  const value = localStorage.getItem(key);
  if (!value) {
    return defaultValue;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return defaultValue;
  }
};

export const setToLocalStorage = (key: string, value: any) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
};

export const isKeyInLocalStorage = (key: string) => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(key) !== null;
};

export const removeFromLocalStorage = (key: string) => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(key);
};

export const getFromSessionStorage = (key: string, defaultValue: any) => {
  if (typeof window === "undefined") return null;
  const value = sessionStorage.getItem(key);
  if (!value) {
    return defaultValue;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return defaultValue;
  }
};

export const isKeyInSessionStorage = (key: string) => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(key) !== null;
};

export const setToSessionStorage = (key: string, value: any) => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const removeFromSessionStorage = (key: string) => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(key);
};

export const sendStorageEvent = (key: string, oldValue?: string, newValue?: string) => {
  const event = new StorageEvent("storage", {
    key,
    newValue,
    oldValue,
  });
  getWindow().dispatchEvent(event);
};
